import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactPaginate from "react-paginate";
import Axios from "../../services/AxiosInstance";

const DEFAULT_PER_PAGE = Number(process.env.REACT_APP_PER_PAGE) || 10;

// HR detail: header with the HR's non-sensitive info (incl. server-provided
// employee count) + the employees created by that HR only. HR scoping and
// counting are enforced by the backend
// (GET api/admin/hr-list/:hrId/employees); nothing is filtered or counted
// on the client.
const HrDetail = () => {
  const { hrId } = useParams();
  const [hr, setHr] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, setSortKey] = useState("full_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    department: "",
    designation: "",
    status: "Active",
  });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [statusPending, setStatusPending] = useState(null);

  const [toast, setToast] = useState("");
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const fetchHrEmployees = useCallback(
    async (pageNo) => {
      setLoading(true);
      setError("");
      setNotFound(false);
      try {
        const params = new URLSearchParams({
          pageNo: String(pageNo),
          perPage: String(perPage),
          sortkey: sortKey,
          order: sortOrder,
        });
        if (appliedSearch) params.set("search", appliedSearch);
        if (statusFilter) params.set("status", statusFilter);

        const res = await Axios.get(
          `api/admin/hr-list/${encodeURIComponent(hrId)}/employees?${params.toString()}`
        );
        const payload = res.data?.data || {};
        setHr(payload.hr || null);
        setItems(Array.isArray(payload.employees) ? payload.employees : []);
        const pagination = res.data?.pagination || {};
        setTotal(Number(pagination.total) || 0);
        setTotalPages(Number(pagination.totalPages) || 0);
        setPage(Number(pagination.page) || pageNo);
      } catch (err) {
        const httpStatus = err?.response?.status;
        if (httpStatus === 404) {
          setNotFound(true);
          setHr(null);
          setItems([]);
          setTotal(0);
          setTotalPages(0);
        } else {
          setError(
            httpStatus === 401 || httpStatus === 403
              ? "You are not authorized to view this HR. Please log in again."
              : "Failed to load HR employees. Please retry."
          );
          setItems([]);
          setTotal(0);
          setTotalPages(0);
        }
      } finally {
        setLoading(false);
      }
    },
    [hrId, perPage, appliedSearch, statusFilter, sortKey, sortOrder]
  );

  useEffect(() => {
    fetchHrEmployees(page);
  }, [fetchHrEmployees, page]);

  const applyFilters = () => {
    setAppliedSearch(search.trim());
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setStatusFilter("");
    setSortKey("full_name");
    setSortOrder("asc");
    setPage(1);
  };

  const handlePageClick = (event) => {
    setPage(event.selected + 1);
  };

  const openEdit = (emp) => {
    setEditEmp(emp);
    setEditForm({
      full_name: emp.name || "",
      email: emp.email || "",
      mobile: emp.phone || "",
      department: emp.department || "",
      designation: emp.designation || "",
      status: emp.status || "Active",
    });
    setModalError("");
  };

  const saveEdit = async () => {
    if (!editEmp || saving) return;
    setSaving(true);
    setModalError("");
    try {
      await Axios.put(
        `api/admin/hr-list/${encodeURIComponent(hrId)}/employees/${encodeURIComponent(editEmp.id)}`,
        {
          full_name: editForm.full_name,
          email: editForm.email,
          mobile: editForm.mobile,
          department: editForm.department,
          designation: editForm.designation,
          status: editForm.status,
        }
      );
      setEditEmp(null);
      showToast("Employee updated successfully");
      fetchHrEmployees(page);
    } catch (err) {
      setModalError("Failed to update employee. Please check the values and retry.");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (emp, nextStatus) => {
    const activating = nextStatus === "Active";
    const confirmed = window.confirm(
      activating
        ? `Activate ${emp.name || emp.id}? The employee account will be Active again.`
        : `Deactivate ${emp.name || emp.id}? This is a soft deactivation of the employee profile only. ` +
          `Login records, roles and the HR relationship are not modified. ` +
          `The account can be activated again later.`
    );
    if (!confirmed || statusPending) return;
    setStatusPending(emp.id);
    try {
      await Axios.put(
        `api/admin/hr-list/${encodeURIComponent(hrId)}/employees/${encodeURIComponent(emp.id)}/status`,
        { status: nextStatus }
      );
      showToast(
        activating ? "Employee activated successfully" : "Employee deactivated successfully"
      );
      fetchHrEmployees(page);
    } catch (err) {
      const httpStatus = err?.response?.status;
      setError(
        httpStatus === 401 || httpStatus === 403
          ? "You are not authorized to change the employee status. Please log in again."
          : httpStatus === 404
          ? "Employee not found for this HR."
          : `Failed to ${activating ? "activate" : "deactivate"} employee. Please retry.`
      );
    } finally {
      setStatusPending(null);
    }
  };

  // Server-side sorting (same sortkey/order contract as the other Admin
  // tables). Changing sort resets to page 1; search/filter values are kept.
  const handleSort = (key, order) => {
    setSortKey(key);
    setSortOrder(order);
    setPage(1);
  };

  const sortArrows = (key) => (
    <>
      <i
        onClick={() => handleSort(key, "asc")}
        className="fa fa-arrow-up"
        style={{
          fontSize: "10px",
          cursor: "pointer",
          marginLeft: "4px",
          color: sortKey === key && sortOrder === "asc" ? "#ffc107" : undefined,
        }}
      ></i>
      <i
        onClick={() => handleSort(key, "desc")}
        className="fa fa-arrow-down"
        style={{
          fontSize: "10px",
          cursor: "pointer",
          marginLeft: "2px",
          color: sortKey === key && sortOrder === "desc" ? "#ffc107" : undefined,
        }}
      ></i>
    </>
  );

  const statusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "active") return "bg-success";
    if (s === "inactive") return "bg-warning text-dark";
    return "bg-secondary";
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "1280px", margin: "0 auto" }}>
      <Link to="/hr-list" className="btn btn-link ps-0 mb-2">
        &larr; Back to HR LIST
      </Link>
      <h1
        className="text-center mb-4"
        style={{ fontWeight: "700", color: "#0B2B4C" }}
      >
        HR DETAIL
      </h1>

      {hr && (
        <div
          className="card mb-4"
          style={{
            border: "none",
            borderRadius: "15px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "#0B2B4C",
              color: "#fff",
              padding: "15px 25px",
            }}
          >
            <span style={{ fontSize: "20px", fontWeight: "600" }}>
              {hr.name || "HR"}
            </span>
          </div>
          <div className="p-4">
            <div className="row g-3">
              <div className="col-md-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>Email</div>
                <div style={{ fontWeight: 600 }}>{hr.email || "-"}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>Business</div>
                <div style={{ fontWeight: 600 }}>{hr.business_name || "-"}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>Status</div>
                <div>
                  {hr.status ? (
                    <span className={`badge ${statusBadge(hr.status)}`}>
                      {hr.status}
                    </span>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>Employees</div>
                <div>
                  <span className="badge bg-primary">{hr.employeeCount ?? 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="card mb-4"
        style={{
          border: "none",
          borderRadius: "15px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "#0B2B4C",
            color: "#fff",
            padding: "15px 25px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "20px", fontWeight: "600" }}>
            Employees Created By This HR
          </span>
          <span style={{ fontSize: "14px", opacity: 0.85 }}>
            {total} record{total === 1 ? "" : "s"}
          </span>
        </div>

        <div className="p-4">
          <div className="row g-3 hr-list-filters">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search employee name, email or id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFilters();
                }}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-3 d-flex gap-2">
              <button className="btn btn-primary flex-fill" onClick={applyFilters}>
                Search
              </button>
              <button
                className="btn btn-secondary flex-fill"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
            <div className="col-md-3 d-flex align-items-center gap-2">
              <label className="mb-0" htmlFor="hr-detail-per-page">
                Per page:
              </label>
              <select
                id="hr-detail-per-page"
                className="form-control"
                style={{ maxWidth: "110px" }}
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value) || DEFAULT_PER_PAGE);
                  setPage(1);
                }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-outline-primary"
                onClick={() => fetchHrEmployees(page)}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2 text-muted">Loading employees...</div>
        </div>
      ) : notFound ? (
        <div className="alert alert-warning text-center">
          HR not found or not a Partner Portal HR user.{" "}
          <Link to="/hr-list">Back to HR LIST</Link>
        </div>
      ) : error ? (
        <div className="alert alert-danger text-center">
          {error}{" "}
          <button
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => fetchHrEmployees(page)}
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="alert alert-info text-center">
          No employees found for this HR.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                <th>S.No</th>
                <th style={{ minWidth: "160px" }}>Employee Name{sortArrows("full_name")}</th>
                <th style={{ minWidth: "200px" }}>Email</th>
                <th style={{ minWidth: "120px" }}>Phone</th>
                <th>Department</th>
                <th>Designation</th>
                <th>Status</th>
                <th style={{ minWidth: "240px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((emp, index) => (
                <tr key={emp.id || index}>
                  <td>{(page - 1) * perPage + index + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {emp.name || "-"}
                    </div>
                    {emp.id && (
                      <div className="text-muted" style={{ fontSize: "12px" }}>
                        {emp.id}
                      </div>
                    )}
                  </td>
                  <td>{emp.email || "-"}</td>
                  <td>{emp.phone || "-"}</td>
                  <td>{emp.department || "-"}</td>
                  <td>{emp.designation || "-"}</td>
                  <td>
                    {emp.status ? (
                      <span className={`badge ${statusBadge(emp.status)}`}>
                        {emp.status}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={{ minWidth: "240px" }}>
                    <div className="hr-list-actions">
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => openEdit(emp)}
                      >
                        <span aria-hidden="true">✏</span> Update
                      </button>
                      {String(emp.status || "").toLowerCase() === "inactive" ? (
                        <button
                          className="btn btn-success btn-sm"
                          disabled={statusPending === emp.id}
                          onClick={() => toggleStatus(emp, "Active")}
                        >
                          <span aria-hidden="true">▶</span> Activate
                        </button>
                      ) : (
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={statusPending === emp.id}
                          onClick={() => toggleStatus(emp, "Inactive")}
                        >
                          <span aria-hidden="true">⏸</span> Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="d-flex flex-column align-items-center mt-3">
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            breakLabel="..."
            pageCount={totalPages}
            forcePage={Math.min(page, totalPages) - 1}
            onPageChange={handlePageClick}
            containerClassName="pagination justify-content-center"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
            disabledClassName="disabled"
          />
          <div className="text-muted" style={{ fontSize: "14px" }}>
            Page {page} of {totalPages} ({total} records)
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: "fixed",
          bottom: "65px",
          right: "20px",
          background: "#28a745",
          color: "white",
          padding: "12px 20px",
          borderRadius: "6px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          zIndex: 2000
        }}>
          {toast}
        </div>
      )}

      {editEmp && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000
        }}>
          <div className="hr-list-filters" style={{
            background: "white",
            padding: "25px",
            borderRadius: "8px",
            width: "440px",
            maxWidth: "92%"
          }}>
            <h4>Edit Employee</h4>

            {modalError && (
              <div className="alert alert-danger">{modalError}</div>
            )}

            <label className="form-label mt-2 mb-0" htmlFor="emp-edit-name">
              Full Name
            </label>
            <input
              id="emp-edit-name"
              className="form-control mb-2"
              value={editForm.full_name}
              onChange={(e) =>
                setEditForm({ ...editForm, full_name: e.target.value })
              }
            />

            <label className="form-label mt-1 mb-0" htmlFor="emp-edit-email">
              Email
            </label>
            <input
              id="emp-edit-email"
              className="form-control mb-2"
              value={editForm.email}
              onChange={(e) =>
                setEditForm({ ...editForm, email: e.target.value })
              }
            />

            <label className="form-label mt-1 mb-0" htmlFor="emp-edit-mobile">
              Mobile Number
            </label>
            <input
              id="emp-edit-mobile"
              className="form-control mb-2"
              value={editForm.mobile}
              onChange={(e) =>
                setEditForm({ ...editForm, mobile: e.target.value })
              }
            />

            <label className="form-label mt-1 mb-0" htmlFor="emp-edit-department">
              Department
            </label>
            <input
              id="emp-edit-department"
              className="form-control mb-2"
              value={editForm.department}
              onChange={(e) =>
                setEditForm({ ...editForm, department: e.target.value })
              }
            />

            <label className="form-label mt-1 mb-0" htmlFor="emp-edit-designation">
              Designation
            </label>
            <input
              id="emp-edit-designation"
              className="form-control mb-2"
              value={editForm.designation}
              onChange={(e) =>
                setEditForm({ ...editForm, designation: e.target.value })
              }
            />

            <label className="form-label mt-1 mb-0" htmlFor="emp-edit-status">
              Status
            </label>
            <select
              id="emp-edit-status"
              className="form-control mb-3"
              value={editForm.status}
              onChange={(e) =>
                setEditForm({ ...editForm, status: e.target.value })
              }
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            <div className="d-flex">
              <button
                className="btn btn-success"
                disabled={saving}
                onClick={saveEdit}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                className="btn btn-danger ms-2"
                disabled={saving}
                onClick={() => setEditEmp(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HrDetail;
